import { useQuery } from "@tanstack/react-query";

import NotificationService from "../services/NotificationService.ts";
import utilService from "@Utils/utils.service";

type FetchingListingApiType = {
  queryParams: any;
  queryKeyValue: string;
  apiService: (param: any) => Promise<any>;
  staleTimeGiven?: number;
  enabled?: boolean;
};
const GENERAL_ERROR = "Something went wrong. Please try again later.";
export const useFetchingListingApi = ({
  apiService,
  queryParams,
  queryKeyValue,
  staleTimeGiven,
  enabled = true,
}: FetchingListingApiType) => {
  // Convert date fields if they exist (for backward compatibility with other components)
  const startDate =
    queryParams.StartDate && queryParams.StartDate !== null
      ? utilService.fromDateToUTC(queryParams.StartDate)
      : queryParams.StartDate;
  const endDate =
    queryParams.EndDate && queryParams.EndDate !== null
      ? utilService.fromDateToUTC(queryParams.EndDate)
      : queryParams.EndDate;
  const joinedFrom = queryParams.JoinedFrom; // Already in YYYY-MM-DD format
  const joinedTo = queryParams.JoinedTo; // Already in YYYY-MM-DD format

  // Clean query params - remove null/undefined values
  const cleanParams = Object.entries({
    ...queryParams,
    ...(startDate !== undefined && { StartDate: startDate }),
    ...(endDate !== undefined && { EndDate: endDate }),
    ...(joinedFrom !== undefined && { JoinedFrom: joinedFrom }),
    ...(joinedTo !== undefined && { JoinedTo: joinedTo }),
  }).reduce(
    (acc, [key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, any>,
  );

  const { data, isError, refetch, isFetching } = useQuery({
    queryKey: [queryKeyValue, cleanParams],
    queryFn: async () => {
      const { ok, response, data } = await apiService(cleanParams);
      if (ok) {
        return data;
      }
      NotificationService.error(response?.message || GENERAL_ERROR);
      throw new Error(GENERAL_ERROR);
    },
    ...(staleTimeGiven && { staleTime: staleTimeGiven }),
    enabled,
  });

  return {
    data,
    isError,
    refetch,
    isFetching,
  };
};
